import React, { useEffect, useState, useRef } from "react";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import ImageGallery from 'react-image-gallery';
import "react-image-gallery/styles/css/image-gallery.css";
import { useTranslation } from "react-i18next";
import SliderContainer from "../slider-images/SliderContainer";
import DeleteImageSlider from "../slider-images/delete-image-slider";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { ProgressBar } from "../slider-images/progress-bar";


const BeforeAfterSlider: React.FunctionComponent<{ images: any, onDelete: any, onSelectedImages: any, applaying: boolean, setapplying: any, processedImages: any, processingIds?: (string | number)[], clearSelection?: boolean }> = (props) => {

  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [imageId, setImageId] = useState<any>();
  const [openPopUp, setOpenPopUp] = useState<boolean>(false);
  const { t } = useTranslation();
  const galleryRef = useRef<ImageGallery>(null);

  // Debug log for applaying prop


  const deleteImageHandler = (id: number | string, value: boolean) => {
    setOpenPopUp(value)
    setImageId(id)
  }

  const deleteImage = () => {
    props.onDelete(imageId).then(() => {
      setSelectedIndex(0);
      setOpenPopUp(false)
    }).catch(() => {
      setSelectedIndex(0);
      setOpenPopUp(false)
    })
  }

  // Communicate selected images to parent component
  useEffect(() => {
    if (props.onSelectedImages && selectedImages.length > 0) {
      props.onSelectedImages(selectedImages, selectedIndex);
    }
  }, [selectedImages, selectedIndex]);

  // Clear selection when parent requests it
  useEffect(() => {
    if (props.clearSelection) {
      setSelectedImages([]);
    }
  }, [props.clearSelection]);

  useEffect(() => {

    if (!props.images || props.images.length === 0) return;

    setIsLoading(true);

    const updatedGalleryImages = props.images.map((img: any) => ({
      original: img.originalImage,
      thumbnail: img.src,
      renderItem: () => (
        <SliderContainer image={img} height="7px" />
      ),
      renderThumbInner: () => {
        // Defensive check for doneEnhancments
        const doneEnhancmentsArr = Array.isArray(img.doneEnhancments) ? img.doneEnhancments : [];
        // Prefer parent-driven processingIds if provided for precise control
        const isProcessing = Array.isArray(props.processingIds)
          ? props.processingIds.includes(img.id)
          : (
            props.applaying &&
            selectedImages.some((selected: any) => selected.id === img.id) &&
            (!props.processedImages || !props.processedImages.some((processed: any) => processed.id === img.id))
          );

        return (
          <div className="thumbnail-container" style={{ position: 'relative' }}>
            <img src={img.originalImage} alt="thumbnail" className="thumbnail-image" />

            {/* Spinner overlay when applying is true, image is selected AND not yet enhanced */}
            {isProcessing && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.5)',
                  zIndex: 2
                }}
              >
                <div className="spinner-border text-primary size-20" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            {
              (img.image_quality_score  || img.image_sizekb || img.brightness) && (
                <ProgressBar image={img} height="5px" ItemProgressContainer="progress-container-thunbinner mt-2" ItemProgressBar="progress-bar-thunbinner" />
              )
            }

            <div className="progress-container-thunbinner">
              <div className="checkbox-overlay">
                <input
                  type="checkbox"
                  className="form-check-input"
                  disabled={doneEnhancmentsArr.includes("Image Super Resolution")}
                  onChange={() => toggleSelectImage(img)}
                  checked={selectedImages.some((selected: any) => selected.id === img.id)}
                />
              </div>
            </div>

            <div className="delete-btn-wrapper">
              <DeleteImageSlider
                images={props.images}
                imageId={img.id}
                onDeleteImage={deleteImageHandler}
              />
            </div>
          </div>
        );
      }

    }));

    setGalleryImages(updatedGalleryImages);
    setIsLoading(false);

    props.onSelectedImages(selectedImages, selectedIndex);

  }, [props.images, selectedImages, selectedIndex, props.applaying, props.processedImages, props.processingIds]);

  useEffect(() => {
    if (!props.processedImages || props.processedImages.length === 0) return;
    setSelectedImages(prev => prev.filter((sel: any) => !props.processedImages.some((p: any) => p.id === sel.id)));
  }, [props.processedImages]);

  useEffect(() => {
    setSelectedImages(prev =>
      prev.filter(
        (sel: any) => {
          const img = props.images.find((i: any) => i.id === sel.id);
          return img && Array.isArray(img.doneEnhancments) && !img.doneEnhancments.includes("Image Super Resolution");
        }
      )
    );
  }, [props.images]);

  const handleSlideChange = (index: number) => {
    setSelectedIndex(index);
  };

  // Navigation functions for thumbnail arrows
  const handleThumbnailPrev = () => {
    if (galleryRef.current) {
      galleryRef.current.slideToIndex(Math.max(selectedIndex - 1, 0));
    }
  };

  const handleThumbnailNext = () => {
    if (galleryRef.current && galleryImages.length > 0) {
      galleryRef.current.slideToIndex(Math.min(selectedIndex + 1, galleryImages.length - 1));
    }
  };

  const toggleSelectImage = (image: any) => {
    setSelectedImages((prevSelected) => {
      const isAlreadySelected = prevSelected.some((img: any) => img.id === image.id);
      return isAlreadySelected
        ? prevSelected.filter((img: any) => img.id !== image.id)
        : [...prevSelected, image];
    });
  };

  const toggleSelectAll = () => {
    console.log(props.images)
    const selectableImages = props.images.filter(
      (img: any) => !((Array.isArray(img.doneEnhancments) && img.doneEnhancments.includes("Image Super Resolution")))
    );
    if (
      selectableImages.every((img: any) =>
        selectedImages.some((selected: any) => selected.id === img.id)
      )
    ) {
      setSelectedImages(prev => prev.filter((selected: any) =>
        !selectableImages.some((img: any) => img.id === selected.id)
      ));
    } else {
      const newSelected = [
        ...selectedImages.filter(
          (selected: any) =>
            !selectableImages.some((img: any) => img.id === selected.id)
        ),
        ...selectableImages
      ];
      setSelectedImages(newSelected);
    }
  };


  return (
    <>
      <div className="gallery-wrapper">
        {isLoading ? (
          <p>{t("startUploading.loadingImages")}</p>
        ) : (
          <div className="image-gallery-container" style={{ position: 'relative' }}>
            <ImageGallery
              ref={galleryRef}
              items={galleryImages}
              showPlayButton={false}
              showFullscreenButton={false}
              onSlide={handleSlideChange}
              startIndex={selectedIndex}
              showThumbnails={true}
              showNav={true}
            />
            {/* Thumbnail navigation arrows overlaying the thumbnail area */}
            {galleryImages.length > 1 && (
              <>
                {/* Left Arrow - overlaying thumbnail area */}
                <button
                  type="button"
                  className="thumbnail-nav-btn thumbnail-nav-prev"
                  disabled={selectedIndex === 0}
                  onClick={handleThumbnailPrev}
                  aria-label="Previous thumbnail"
                  style={{
                    position: 'absolute',
                    bottom: '35px',
                    left: '15px',
                    zIndex: 999,
                    background: selectedIndex === 0 ? 'rgba(0, 0, 0, 0.5)' : '#746AFF',
                    border: '2px solid white',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: selectedIndex === 0 ? 'not-allowed' : 'pointer',
                    opacity: selectedIndex === 0 ? 0.5 : 1,
                    transition: 'all 0.3s ease',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.5)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedIndex !== 0) {
                      e.currentTarget.style.background = '#746AFF';
                      e.currentTarget.style.transform = 'scale(1.2)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.6)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedIndex !== 0) {
                      e.currentTarget.style.background = '#746AFF';
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.5)';
                    }
                  }}
                >
                  <ChevronLeft size={20} />
                </button>

                {/* Right Arrow - overlaying thumbnail area */}
                <button
                  type="button"
                  className="thumbnail-nav-btn thumbnail-nav-next"
                  disabled={selectedIndex === galleryImages.length - 1}
                  onClick={handleThumbnailNext}
                  aria-label="Next thumbnail"
                  style={{
                    position: 'absolute',
                    bottom: '40px',
                    right: '15px',
                    zIndex: 999,
                    background: selectedIndex === galleryImages.length - 1 ? 'rgba(0, 0, 0, 0.5)' : '#746AFF',
                    border: '2px solid white',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: selectedIndex === galleryImages.length - 1 ? 'not-allowed' : 'pointer',
                    opacity: selectedIndex === galleryImages.length - 1 ? 0.5 : 1,
                    transition: 'all 0.3s ease',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.5)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedIndex !== galleryImages.length - 1) {
                      e.currentTarget.style.background = '#746AFF';
                      e.currentTarget.style.transform = 'scale(1.2)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.6)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedIndex !== galleryImages.length - 1) {
                      e.currentTarget.style.background = '#746AFF';
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.5)';
                    }
                  }}
                >
                  <ChevronRight size={20} />
                </button>

                {/* Image counter overlaying thumbnail area */}
                <div style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10,
                  background: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  fontSize: '12px',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontWeight: '500'
                }}>
                  {selectedIndex + 1} / {galleryImages.length}
                </div>
              </>
            )}
          </div>)
        }
        <div className="select-all-wrapper">
          <input type="checkbox" className="form-check-input" onChange={toggleSelectAll} checked={
            props.images.filter((img: any) => Array.isArray(img.doneEnhancments) && !img.doneEnhancments.includes("Image Super Resolution")).length > 0 &&
            props.images.filter((img: any) => Array.isArray(img.doneEnhancments) && !img.doneEnhancments.includes("Image Super Resolution")).every(
              (img: any) => selectedImages.some((selected: any) => selected.id === img.id)
            )
          } />
          <span className="select-all-text">{t("enhanceAndGenerate.selectAll")}</span>
        </div>
      </div>

      <Dialog
        open={openPopUp}
        onClose={() => setOpenPopUp(false)}
        fullWidth={true}
      >
        <DialogTitle>
          <div className="title-18">{t("propertiesPage.confirmation")}</div>
        </DialogTitle>
        <DialogContent>
          <p className="para-16  text-start">{t("listingCreation.msgDeleteImage")}</p>
        </DialogContent>
        <DialogActions>
          <button type="button" className="button-dash btn-outline-secondary back-trans padd-15 padd-5-15-mb" onClick={() => setOpenPopUp(false)}>
            {t("propertiesPage.no")}
          </button>
          <button type="button" className="button-dash padd-15 padd-5-15-mb btn-danger" onClick={deleteImage}>
            {t("propertiesPage.yes")}
          </button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default BeforeAfterSlider;
